import { FundingPolicyType } from './appModel';
import { CARRIER_NAMES } from './constants';

export enum InsuranceCarrier {
  Nationwide = 4501,
  OneAmerica = 4502,
  CareScout = 4503,
  LincolnFinancial = 4504,
  SecurianFinancial = 4505,
  Transamerica = 4506,
  WesternHome = 4507,
  NewYorkLife = 4508,
  GlobalAtlantic = 4509,
  Thrivent = 4510,
  NGL = 4511,
  MutualOfOmaha = 4512,
  Trustmark = 4513,
  Allstate = 4514,
  EquiTrust = 4515,
  StayYourWay = 4516,
  ManhattanLife = 4517,
}

type InsuranceCarrierDef = {
  name: string; // this is the name of the carrier as it appears in the database. Need to figure out how to sync
  policyTypes: FundingPolicyType[];
  supportsPUI?: boolean;
  puiEmail?: string;
  image?: {
    src: string;
    size: string; // Tailwind classes
  };
};

export const insuranceCarrierDefs: Record<InsuranceCarrier, InsuranceCarrierDef> = {
  [InsuranceCarrier.Transamerica]: {
    name: 'Transamerica',
    policyTypes: [FundingPolicyType.lifeWithIndexAccount],
    image: {
      src: '/logos/transamerica-logo.jpg',
      size: 'h-auto w-40 md:w-44',
    },
  },
  [InsuranceCarrier.WesternHome]: {
    name: 'Western Home',
    policyTypes: [FundingPolicyType.fortifiedLife],
    image: {
      src: '/logos/whclogopng.webp',
      size: 'h-auto w-36 md:w-40',
    },
  },
  [InsuranceCarrier.NewYorkLife]: {
    name: 'New York Life',
    policyTypes: [
      FundingPolicyType.nylSecureCare,
      FundingPolicyType.nylAssetFlex,
      FundingPolicyType.nylJointSecureCare,
    ],
    image: {
      src: '/logos/nyl-logo.png',
      size: 'h-auto w-14 md:w-16',
    },
    supportsPUI: true,
    puiEmail: 'rapid_response@newyorklife.com',
  },
  [InsuranceCarrier.SecurianFinancial]: {
    name: 'Securian Financial',
    policyTypes: [FundingPolicyType.secureCareThree, FundingPolicyType.secureCareFour],
    supportsPUI: true,
    puiEmail: 'securecarequickquote@securian.com',
    image: {
      src: '/logos/securian-logo.png',
      size: 'h-auto w-32 md:w-36',
    },
  },
  [InsuranceCarrier.LincolnFinancial]: {
    name: CARRIER_NAMES.LINCOLN_FINANCIAL,
    policyTypes: [FundingPolicyType.moneyGuardFixedAdvantage],
    image: {
      src: '/logos/lincoln-financial-logo.png',
      size: 'h-auto w-24 md:w-28',
    },
  },
  [InsuranceCarrier.OneAmerica]: {
    name: CARRIER_NAMES.ONE_AMERICA,
    policyTypes: [
      FundingPolicyType.assetCare,
      FundingPolicyType.jointAssetCare,
      FundingPolicyType.annuityCareTwo,
      FundingPolicyType.assetcareThree,
      FundingPolicyType.jointAssetcareThree,
      FundingPolicyType.indexedAnnuityCare,
    ],
    supportsPUI: true,
    puiEmail: 'CSPUI@oneamerica.com',
    image: {
      src: '/logos/oneamerica-mark.svg',
      size: 'h-auto w-28 md:w-32',
    },
  },
  [InsuranceCarrier.Nationwide]: {
    name: CARRIER_NAMES.NATIONWIDE,
    policyTypes: [
      FundingPolicyType.careMattersTwo,
      FundingPolicyType.jointCareMatters,
      FundingPolicyType.noLapseGuaranteeLife,
      // Gated on quoteApplyCareMattersAnnuity — see POLICY_TYPE_FEATURE_FLAG_MAP in
      // insuranceCarrierFeatureFlags.ts. Listing them here is what makes the flag
      // meaningful: quote filtering works off this list, so an unlisted policy type
      // is dropped unconditionally and the flag would have nothing to switch on.
      FundingPolicyType.careMattersAnnuity,
      FundingPolicyType.jointCareMattersAnnuity,
    ],
    supportsPUI: true,
    puiEmail: 'caremattersscreen@nationwide.com',
    image: {
      src: '/logos/nationwide-mark.svg',
      size: 'h-auto w-28 md:w-32',
    },
  },
  [InsuranceCarrier.GlobalAtlantic]: {
    name: 'Global Atlantic',
    policyTypes: [
      FundingPolicyType.forecareFixedAnnuity,
      FundingPolicyType.jointForecareFixedAnnuity,
    ],
    image: {
      src: '/logos/global-atlantic-logo.jpg',
      size: 'h-auto w-40 md:w-44',
    },
  },
  [InsuranceCarrier.Thrivent]: {
    name: 'Thrivent',
    policyTypes: [
      FundingPolicyType.thriventLongTermCareInsurance,
      FundingPolicyType.jointThriventLongTermCareInsurance,
    ],
    image: {
      src: '/logos/thrivent-logo.jpg',
      size: 'h-auto w-28 md:w-32',
    },
  },
  [InsuranceCarrier.NGL]: {
    name: 'NGL',
    policyTypes: [FundingPolicyType.nglEssentialLtc, FundingPolicyType.jointNglEssentialLtc],
    image: {
      src: '/logos/ngl-logo.png',
      size: 'h-auto w-28 md:w-32',
    },
  },
  [InsuranceCarrier.MutualOfOmaha]: {
    name: 'Mutual of Omaha',
    policyTypes: [
      FundingPolicyType.mutualOfOmahaMutualcare,
      FundingPolicyType.jointMutualOfOmahaMutualcare,
    ],
    image: {
      src: '/logos/moo-logo.png',
      size: 'h-auto w-40 md:w-44',
    },
  },
  [InsuranceCarrier.CareScout]: {
    name: 'Care Assurance',
    policyTypes: [FundingPolicyType.careScoutCareAssurance],
    supportsPUI: true,
    puiEmail: 'uwprequalification@carescout.com',
    image: {
      src: '/logos/carescout.svg',
      size: 'h-auto w-40 md:w-44',
    },
  },
  [InsuranceCarrier.Trustmark]: {
    name: 'Trustmark',
    policyTypes: [FundingPolicyType.agisGroupPolicy],
    image: {
      src: '/logos/oneamerica-mark.svg', // Using OneAmerica as placeholder since Trustmark logo not in current mapping
      size: 'h-auto w-28 md:w-32',
    },
  },
  [InsuranceCarrier.Allstate]: {
    name: 'Allstate',
    policyTypes: [FundingPolicyType.allstateHybridPolicy],
    image: {
      src: '/logos/oneamerica-mark.svg', // Using OneAmerica as placeholder since Allstate logo not in current mapping
      size: 'h-auto w-28 md:w-32',
    },
  },
  [InsuranceCarrier.EquiTrust]: {
    name: 'EquiTrust',
    policyTypes: [FundingPolicyType.equitrustBridge, FundingPolicyType.annuityHybrid],
    image: {
      src: '/logos/equitrust-logo.png', // TODO: Add actual EquiTrust logo
      size: 'h-auto w-32 md:w-36',
    },
  },
  [InsuranceCarrier.StayYourWay]: {
    name: 'Stay Your Way',
    policyTypes: [FundingPolicyType.stayYourWay],
    image: {
      src: '/logos/whclogopng.webp', // TODO: Add Stay Your Way logo
      size: 'h-auto w-36 md:w-40',
    },
  },
  [InsuranceCarrier.ManhattanLife]: {
    name: 'ManhattanLife',
    policyTypes: [FundingPolicyType.manhattanLifeOmniFlexStc],
    image: {
      // The full lockup (Lady Liberty + wordmark) rather than the square
      // Lady Liberty mark, which also ships as manhattanlife-mark.png: the
      // mark alone carries no company name, and every sibling carrier here is
      // a wordmark. Sized down to match their on-screen weight.
      src: '/logos/manhattanlife-logo.png',
      size: 'h-auto w-32 md:w-36',
    },
  },
};
